// Translation layer between the backend wire shape (UPPERCASE enums, UUID ids,
// eventDate / paymentDate field names) and the mock-style shape the rest of
// the app already uses (lowercase enums, `date` field). Keeping this in one
// place means every API module looks the same and components don't change.

const toLower = (v) => (typeof v === 'string' ? v.toLowerCase() : v);
const toUpper = (v) => (typeof v === 'string' ? v.toUpperCase() : v);

// ── Members ────────────────────────────────────────────────────────
export function memberFromApi(m) {
  if (!m) return m;
  return {
    id: m.id,
    code: m.code,
    firstName: m.firstName,
    lastName: m.lastName,
    phone: m.phone || '',
    email: m.email || '',
    address: m.address || '',
    joinedAt: m.joinedAt,
    status: toLower(m.status),
    notes: m.notes || '',
    parish: m.parish || '',
    gender: toLower(m.gender) || '',
    maritalStatus: toLower(m.maritalStatus) || '',
    nationalId: m.nationalId || '',
    dateOfBirth: m.dateOfBirth || '',
    totalPaid: m.totalPaid != null ? Number(m.totalPaid) : 0,
    createdAt: m.createdAt,
  };
}

export function memberToApi(m) {
  return {
    firstName: m.firstName,
    lastName: m.lastName,
    phone: m.phone,
    email: m.email || null,
    address: m.address || null,
    joinedAt: m.joinedAt,
    status: toUpper(m.status),
    notes: m.notes || null,
    parish: m.parish || null,
    gender: m.gender ? toUpper(m.gender) : null,
    maritalStatus: m.maritalStatus ? toUpper(m.maritalStatus) : null,
    // IDs are often typed with spaces ("1 1990 8 ..."); the backend wants
    // plain alphanumerics.
    nationalId: m.nationalId ? String(m.nationalId).replace(/\s+/g, '') : null,
    dateOfBirth: m.dateOfBirth || null,
  };
}

// ── Events ─────────────────────────────────────────────────────────
export function eventFromApi(e) {
  if (!e) return e;
  return {
    id: e.id,
    code: e.code,
    name: e.name,
    destination: e.destination,
    date: e.eventDate,
    capacity: e.capacity,
    registered: e.registered,
    price: e.price != null ? Number(e.price) : 0,
    status: toLower(e.status),
    description: e.description || '',
    autoMonth: e.autoMonth || null,
    bookingReopened: Boolean(e.bookingReopened),
    bookingOpen: Boolean(e.bookingOpen),
    bookingClosesAt: e.bookingClosesAt || null,
    createdAt: e.createdAt,
  };
}

export function eventToApi(e) {
  return {
    name: e.name,
    destination: e.destination,
    eventDate: e.date,
    capacity: e.capacity,
    registered: e.registered,
    price: e.price,
    status: toUpper(e.status),
    description: e.description || null,
  };
}

// ── Payments ───────────────────────────────────────────────────────
export function paymentFromApi(p) {
  if (!p) return p;
  return {
    id: p.id,
    code: p.code,
    memberId: p.memberId || null,
    memberName: p.memberName || null,
    payerName: p.payerName || null,
    eventId: p.eventId || null,
    eventName: p.eventName || null,
    type: toLower(p.type),
    amount: p.amount != null ? Number(p.amount) : 0,
    date: p.paymentDate,
    method: toLower(p.method),
    status: toLower(p.status),
    notes: p.notes || '',
    externalRef: p.externalRef || '',
    createdAt: p.createdAt,
  };
}

export function paymentToApi(p) {
  return {
    memberId: p.memberId || null,
    payerName: p.payerName || null,
    eventId: p.eventId || null,
    type: toUpper(p.type),
    amount: p.amount,
    paymentDate: p.date,
    method: toUpper(p.method),
    status: toUpper(p.status),
    notes: p.notes || null,
  };
}

// ── Reservations (website bookings) ────────────────────────────────
const toNumber = (v) => (v != null ? Number(v) : 0);

export function reservationFromApi(r) {
  if (!r) return r;
  return {
    ...r,
    payMethod: toLower(r.payMethod),
    status: toLower(r.status),
    peopleCount: toNumber(r.peopleCount),
    unitPrice: toNumber(r.unitPrice),
    feePerPerson: toNumber(r.feePerPerson),
    totalAmount: toNumber(r.totalAmount),
    participants: (r.participants || [])
      .map((p) => ({ ...p, gender: toLower(p.gender) || '' }))
      .sort((a, b) => (a.position || 0) - (b.position || 0)),
  };
}

// ── Payouts ────────────────────────────────────────────────────────
export function payoutFromApi(p) {
  if (!p) return p;
  return {
    ...p,
    status: toLower(p.status),
    amount: toNumber(p.amount),
    txnCharge: p.txnCharge != null ? Number(p.txnCharge) : null,
  };
}

export function payoutOverviewFromApi(o) {
  if (!o) return o;
  return {
    ...o,
    collected: toNumber(o.collected),
    paidOut: toNumber(o.paidOut),
    available: toNumber(o.available),
  };
}

// ── Documents (minutes & reports) ──────────────────────────────────
export function documentFromApi(d) {
  if (!d) return d;
  return {
    ...d,
    category: toLower(d.category),
    meetingDate: d.meetingDate || '',
    location: d.location || '',
    attendees: d.attendees || '',
    contentHtml: d.contentHtml || '',
  };
}

export function documentToApi(d) {
  return {
    title: d.title,
    category: toUpper(d.category),
    meetingDate: d.meetingDate || null,
    location: d.location || null,
    attendees: d.attendees || null,
    contentHtml: d.contentHtml || '',
  };
}

// ── Users ──────────────────────────────────────────────────────────
export function userFromApi(u) {
  if (!u) return u;
  return {
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    username: u.username,
    email: u.email || '',
    phone: u.phone || '',
    role: u.role,
    roleId: u.roleId,
    permissions: u.permissions || [],
    active: u.active,
    lastLoginAt: u.lastLoginAt,
    createdAt: u.createdAt,
  };
}

export function createUserToApi(u) {
  return {
    firstName: u.firstName,
    lastName: u.lastName,
    username: u.username,
    email: u.email || null,
    phone: u.phone,
    roleId: u.roleId,
    password: u.password,
  };
}

export function updateUserToApi(u) {
  const payload = {
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email || null,
    phone: u.phone,
    roleId: u.roleId,
  };
  if (u.password) payload.password = u.password;
  return payload;
}

// ── Activity (dashboard) ───────────────────────────────────────────
export function activityFromApi(a) {
  if (!a) return a;
  return {
    id: a.id,
    action: a.action,
    entityType: a.entityType,
    entityId: a.entityId,
    description: a.description,
    username: a.username,
    at: a.at,
    // Mock used `dot` + `textKey` + `params` to render i18n strings. The
    // backend returns a pre-rendered `description` — components fall back
    // to it when textKey is absent.
    dot: dotForAction(a.action),
  };
}

function dotForAction(action) {
  if (!action) return 'blue';
  const a = action.toLowerCase();
  if (a.includes('member') || a.includes('create')) return 'green';
  if (a.includes('payment') || a.includes('paid')) return 'gold';
  return 'blue';
}
